import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatControls } from '../../chat/ChatView'
import type { FanData } from '../../chat/useFan'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useMediaSrc } from '../../library/useMediaSrc'
import { HeaderDialogs, type HeaderPermissions } from '../ChatHeader'
import { useHeaderActions } from '../headerActions'
import type { ChatRow } from '../chatRows'
import { Icon } from '../Icon'
import { PhoneSheet } from './PhoneSheet'
import { Spinner } from '../../components/Spinner'
import './phone.css'

interface Props {
  accountId: string
  fanId: string
  fan: FanData
  chat: ChatControls
  nameHint: string | null
  can: HeaderPermissions
  onLeave: () => void
  onBack: () => void
  /** The list's rows: the avatar rail, and the Back badge (other unread chats). */
  siblings: ChatRow[]
  onOpenSibling: (fanId: string, name: string) => void
  /** Mark unread on the list and go back (after OnlyFans took it). */
  onMarkUnread: (() => void) | null
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/**
 * The open chat's top on a phone (handoff-19): Back with the other unread
 * chats, the fan (tap: Fan info), Pin and More; under it the avatar rail of
 * the chats from the list, to jump between them without going back.
 */
export function PhoneChatHeader({ accountId, fanId, fan, chat, nameHint, can, onLeave, onBack, siblings, onOpenSibling, onMarkUnread }: Props) {
  const { t } = useTranslation('chats')
  const h = useHeaderActions({ accountId, fanId, fan, nameHint, onLeave })
  const [sheet, setSheet] = useState<'more' | 'lists' | null>(null)
  const pic = useMediaSrc(h.p?.avatar ?? '')
  const othersUnread = siblings.filter((r) => r.fanId !== fanId && r.unread > 0).length
  const online = h.presence?.online === true
  const close = () => setSheet(null)
  /** A More row: does its thing and closes the sheet. */
  const item = (icon: React.ReactNode, label: string, run: () => void, danger = false) => (
    <button
      type="button"
      className={`ph-menu-item ${danger ? 'is-danger' : ''}`}
      onClick={() => {
        close()
        run()
      }}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div className="ph-chat-head" role="group" aria-label={t('header.label', { name: h.name })}>
      <div className="ph-chat-bar">
        <button type="button" className="ph-back" aria-label={othersUnread ? t('phone.backUnread', { count: othersUnread }) : t('backToChats')} onClick={onBack}>
          <FeatherIcon name="chevron-left" size={26} />
          {othersUnread > 0 && <span className="ph-back-badge">{othersUnread > 99 ? '99+' : othersUnread}</span>}
        </button>
        <button type="button" className="ph-chat-who" aria-label={t('phone.fanInfoFor', { name: h.name })} onClick={() => chat.side?.toggle()}>
          <span className="ph-chat-avatar">
            <span className="ph-chat-pic" ref={pic.ref as React.RefObject<HTMLSpanElement>}>
              {pic.src ? <img src={pic.src} alt="" /> : initialsOf(h.name)}
            </span>
            {online && <span className="ph-row-online is-head" aria-hidden="true" />}
          </span>
          <span className="ph-chat-names">
            <span className="ph-chat-name">
              <span>{h.name}</span>
              <FeatherIcon name="chevron-down" size={14} />
            </span>
            {h.presence && <span className={`ph-chat-seen ${online ? 'is-online' : ''}`}>{h.presence.label}</span>}
          </span>
        </button>
        <button type="button" className={`ph-icon-btn ph-chat-pin ${h.pinned ? 'is-on' : ''}`} aria-label={h.pinned ? t('header.unpin') : t('header.pin')} aria-pressed={h.pinned} onClick={() => void h.togglePin()}>
          <Icon id="icon-pin" size={19} />
        </button>
        <button type="button" className="ph-icon-btn ph-chat-more" aria-label={t('more.title')} aria-haspopup="dialog" onClick={() => setSheet('more')}>
          <FeatherIcon name="more-horizontal" size={20} />
        </button>
      </div>
      {siblings.length > 1 && (
        <div className="ph-rail" role="list" aria-label={t('phone.rail')}>
          {siblings.map((r) => (
            <RailChat key={r.fanId} row={r} on={r.fanId === fanId} onOpen={() => r.fanId !== fanId && onOpenSibling(r.fanId, r.name)} />
          ))}
        </div>
      )}
      {h.notice && (
        <div className="ph-chat-notice" role="status">
          <span>{h.notice}</span>
          <button type="button" aria-label={t('common.close')} onClick={() => h.setNotice(null)}>
            <FeatherIcon name="x" size={14} />
          </button>
        </div>
      )}

      <PhoneSheet open={sheet === 'more'} onClose={close} label={t('more.title')}>
        <div className="ph-menu">
          {chat.side && item(<FeatherIcon name="user" size={19} />, t('header.fanInfo'), chat.side.toggle)}
          {can.editLists && item(<FeatherIcon name="star" size={19} />, t('lists.star'), () => setSheet('lists'))}
          {can.writeChats && item(<FeatherIcon name="edit-2" size={19} />, t('rename.title'), () => h.setRenaming(true))}
          {can.writeChats && item(<FeatherIcon name={h.muted ? 'bell' : 'bell-off'} size={19} />, h.muted ? t('header.unmute') : t('header.mute'), () => void h.toggleMute())}
          {can.writeChats && onMarkUnread && item(<FeatherIcon name="eye-off" size={19} />, t('phone.markUnread'), () => void h.markUnread(onMarkUnread))}
          {item(<FeatherIcon name="image" size={19} />, t('header.gallery'), chat.toggleGallery)}
          {item(<FeatherIcon name="search" size={19} />, t('header.find'), chat.toggleFind)}
          {item(<FeatherIcon name="globe" size={19} />, chat.autoTranslate.on ? t('phone.translateOff') : t('phone.translateOn'), chat.autoTranslate.toggle)}
          {item(<FeatherIcon name="refresh-cw" size={19} />, t('more.refresh'), chat.refresh)}
          {can.writeChats && item(<FeatherIcon name="archive" size={19} />, t('more.hide'), () => h.setConfirming('hide'))}
          {can.restrict && item(<FeatherIcon name="slash" size={19} />, t('more.restrict'), () => h.setConfirming('restrict'))}
          {can.writeChats && item(<FeatherIcon name="trash-2" size={19} />, t('more.clear'), () => h.setConfirming('clear'), true)}
        </div>
      </PhoneSheet>

      <PhoneSheet open={sheet === 'lists'} onClose={close} label={t('lists.title')} title={t('lists.title')}>
        <ListsChips fan={fan} />
      </PhoneSheet>

      <HeaderDialogs h={h} fanId={fanId} />
    </div>
  )
}

function RailChat({ row, on, onOpen }: { row: ChatRow; on: boolean; onOpen: () => void }) {
  const pic = useMediaSrc(row.avatar ?? '')
  return (
    <div role="listitem" className="ph-rail-item">
      <button type="button" className={`ph-rail-chat ${on ? 'is-on' : ''}`} aria-current={on ? 'true' : undefined} onClick={onOpen}>
        <span className="ph-rail-avatar">
          <span className="ph-rail-pic" ref={pic.ref as React.RefObject<HTMLSpanElement>}>
            {pic.src ? <img src={pic.src} alt="" loading="lazy" /> : initialsOf(row.named ? row.name : '')}
          </span>
          {row.unread > 0 && !on && <span className="ph-rail-dot" aria-hidden="true" />}
        </span>
        <span className="ph-rail-name">{row.name.split(/\s+/)[0]}</span>
      </button>
    </div>
  )
}

/** The account's OnlyFans lists as the design's chips; ticked where the fan is. */
function ListsChips({ fan }: { fan: FanData }) {
  const { t } = useTranslation('chats')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const toggle = async (listId: string, member: boolean) => {
    setBusy(listId)
    setError(null)
    try {
      await fan.setMembership(listId, member)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(null)
    }
  }
  if (!fan.lists.length) return <span className="ph-picker-note">{fan.loading ? <Spinner size={14} label={t('lists.loading')} /> : t('lists.none')}</span>
  return (
    <div className="ph-lists">
      <div className="ph-lists-chips">
        {fan.lists.map((l) => {
          const on = fan.memberOf.has(l.id)
          return (
            <button key={l.id} type="button" className={`ph-list-chip ${on ? 'is-on' : ''}`} aria-pressed={on} disabled={busy != null} onClick={() => void toggle(l.id, !on)}>
              {on && <FeatherIcon name="check" size={13} />}
              <span translate="no">{l.name}</span>
            </button>
          )
        })}
      </div>
      {error && <span className="ph-lists-error">{error}</span>}
    </div>
  )
}
